/**
 * SOW token validity
 * ──────────────────
 * Detects tokens used in a contract that won't resolve at deal time - a
 * `context.<key>` whose key is not in the catalog (e.g. a deleted questionnaire
 * field) and is not a system namespace. Drives the studio's "unbound" chip state
 * and the publish-readiness warning. (System namespaces are always resolvable, so
 * they are never flagged.)
 */

import type { DocumentContract } from '@protoss/doc';

const SYSTEM_PREFIXES = [
  'date.',
  'brand.',
  'meta.',
  'sow.',
  'config.',
  'item.',
  'asset:',
  'totals.',
  'staffing',
  'partners',
  'discount',
  'paymentSchedule',
];

const TOKEN_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

export interface UsedToken {
  /** Token path without the `protoss:` namespace, e.g. `context.clientName`. */
  path: string;
  /** The catalog key (the `context.` prefix stripped). */
  key: string;
}

/** True for namespaces resolved by the render context (never "unbound"). */
export function isSystemToken(path: string): boolean {
  return SYSTEM_PREFIXES.some((p) => path.startsWith(p));
}

/** Distinct tokens referenced anywhere in the contract. */
export function collectTokens(contract: DocumentContract): UsedToken[] {
  const json = JSON.stringify(contract);
  const seen = new Set<string>();
  const out: UsedToken[] = [];
  for (const m of json.matchAll(TOKEN_RE)) {
    const path = (m[1] ?? '').replace(/^protoss:/, '');
    if (!path || seen.has(path)) continue;
    seen.add(path);
    out.push({ path, key: path.replace(/^context\./, '') });
  }
  return out;
}

/** Is one token resolvable given the catalog keys? */
export function isTokenBound(path: string, catalogKeys: Set<string>): boolean {
  if (isSystemToken(path)) return true;
  if (path.startsWith('context.')) return catalogKeys.has(path.replace(/^context\./, ''));
  // Unknown namespace: conservatively treat as bound (don't false-flag).
  return true;
}

/** Tokens in the contract that won't resolve (orphaned field references). */
export function findUnboundTokens(
  contract: DocumentContract,
  catalogKeys: Set<string>
): UsedToken[] {
  return collectTokens(contract).filter((t) => !isTokenBound(t.path, catalogKeys));
}
